import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShopError, api, type TrackedOrder } from '../api';
import { formatCzk } from '../money';
import { usePageMeta } from '../seo';
import { recallOrder } from '../orderMemory';

/**
 * Sledovani objednavky.
 *
 * Nejcastejsi dotaz do e-mailu je „kde mam objednavku". Stranka na nej
 * odpovi bez toho, aby si zakaznik musel zakladat ucet: staci cislo
 * objednavky a e-mail, kterym objednaval.
 *
 * Dvojice chodi v adrese, takze odkaz z potvrzovaciho e-mailu vede rovnou
 * na vysledek. Stranka se proto neindexuje – viz robots.txt.
 */
export function TrackOrderPage() {
  const [params, setParams] = useSearchParams();
  usePageMeta({ title: 'Sledování objednávky | Super Krmivo', index: false });

  const [orderNumber, setOrderNumber] = useState(params.get('cislo') ?? '');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vyhledani z adresy – odkaz z e-mailu ma rovnou ukazat vysledek.
  const queryNumber = params.get('cislo') ?? '';
  const queryEmail = params.get('email') ?? '';

  useEffect(() => {
    if (queryNumber === '' || queryEmail === '') return;

    let cancelled = false;
    setLoading(true);
    api
      .trackOrder(queryNumber, queryEmail)
      .then((result) => {
        if (cancelled) return;
        setOrder(result.order);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setOrder(null);
        setError(caught instanceof ShopError ? caught.message : 'Objednávku se nepodařilo najít.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryNumber, queryEmail]);

  // Po objednani si vyloha pamatuje cislo i e-mail – predvyplnime je.
  useEffect(() => {
    if (orderNumber !== '' || email !== '') return;
    const remembered = recallOrder(params.get('cislo') ?? '');
    if (remembered !== null) {
      setOrderNumber(remembered.orderNumber);
      setEmail(remembered.email);
    }
  }, [orderNumber, email, params]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setParams({ cislo: orderNumber.trim(), email: email.trim() });
  }

  return (
    <div className="track">
      <h1>Kde je moje objednávka</h1>
      <p className="muted">
        Zadej číslo objednávky a e-mail, kterým jsi objednával. Najdeš je v potvrzovacím e-mailu.
      </p>

      <form className="track-form" onSubmit={submit}>
        <label className="field">
          <span>Číslo objednávky</span>
          <input
            name="orderNumber"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            placeholder="2026000123"
            inputMode="numeric"
            required
          />
        </label>
        <label className="field">
          <span>E-mail</span>
          <input
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="jmeno@example.cz"
            required
          />
        </label>
        <button type="submit" className="button primary" disabled={loading}>
          {loading ? 'Hledám…' : 'Najít objednávku'}
        </button>
      </form>

      {error !== null && <p className="alert" role="alert">{error}</p>}

      {order !== null && <OrderDetail order={order} email={queryEmail} />}
    </div>
  );
}

function OrderDetail({ order, email }: { order: TrackedOrder; email: string }) {
  return (
    <section className="track-result">
      <header className="track-head">
        <div>
          <h2>Objednávka {order.orderNumber}</h2>
          <p className="muted small">Přijata {formatDateTime(order.placedAt)}</p>
        </div>
        <span className={`badge tone-${order.steps.at(-1)?.tone ?? 'gray'}`}>
          {order.statusLabel}
        </span>
      </header>

      <ol className="track-steps">
        {order.steps.map((step) => (
          <li
            key={step.status}
            className={`track-step${step.done ? ' is-done' : ''}${step.current ? ' is-current' : ''}`}
          >
            <span className="track-dot" aria-hidden="true" />
            <div>
              <strong>{step.label}</strong>
              <div className="muted small">
                {step.at === null ? 'čeká nás' : formatDateTime(step.at)}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {order.trackingUrl !== null && (
        <p>
          <a className="button" href={order.trackingUrl} target="_blank" rel="noreferrer">
            Sledovat zásilku u dopravce
          </a>
        </p>
      )}

      <dl className="track-facts">
        <div>
          <dt>Doprava</dt>
          <dd>
            {order.shippingLabel || '—'}
            {order.pickupPointName !== null && (
              <div className="muted small">{order.pickupPointName}</div>
            )}
          </dd>
        </div>
        <div>
          <dt>Platba</dt>
          <dd>
            {order.paymentLabel || '—'}
            <div className="muted small">
              {order.paidAt === null ? 'zatím nezaplaceno' : `zaplaceno ${formatDay(order.paidAt)}`}
            </div>
          </dd>
        </div>
        <div>
          <dt>Celkem</dt>
          <dd>{formatCzk(order.totalMinor)}</dd>
        </div>
      </dl>

      <table className="table">
        <tbody>
          {order.items.map((item, index) => (
            <tr key={`${item.name}-${index}`}>
              <td>
                {item.name}
                <div className="muted small">
                  {item.variantName} · {item.quantity} ks
                </div>
              </td>
              <td className="num">{formatCzk(item.lineTotalMinor)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {order.hasInvoice && (
        <p className="muted small">
          <a href={api.invoiceUrl(order.orderNumber, email)} target="_blank" rel="noreferrer">
            Stáhnout fakturu (PDF)
          </a>
        </p>
      )}
    </section>
  );
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

function formatDay(iso: string): string {
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'short' }).format(new Date(iso));
}
